import { useEffect, useMemo, useRef } from "react";
import { geoNaturalEarth1 } from "d3-geo";
import { getLandPoints, CITIES, LINKS } from "../lib/land";

const PINE = "#0E5C59";
const GOLD = "#C9A84C";
const INK = "#16201F";
const CREAM = "#F7F2E8";

const LABEL_OFFSETS = {
  Milano: { dx: 9, dy: -9 },
  Monaco: { dx: 9, dy: 12 },
  Londra: { dx: -9, dy: -9 },
  Parigi: { dx: -9, dy: 12 },
  Dubai: { dx: -10, dy: -9 },
  "New York": { dx: 10, dy: -9 },
  Mosca: { dx: 10, dy: -9 },
  Singapore: { dx: 10, dy: 12 },
  "Hong Kong": { dx: 10, dy: -10 },
  Tokyo: { dx: -10, dy: -9 },
  Mumbai: { dx: -12, dy: 12 },
  Sydney: { dx: -10, dy: 12 },
  "San Paolo": { dx: 10, dy: 12 },
};

export default function Globe({ className = "" }) {
  const canvasRef = useRef(null);
  const reduced = useMemo(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    let raf = 0;
    let landPts = [];
    let cityPts = [];
    let arcPts = [];
    let w = 0;
    let h = 0;
    let dpr = 1;

    const rebuild = () => {
      const rectW = canvas.clientWidth || canvas.parentElement.clientWidth || 600;
      w = rectW;
      h = Math.round(rectW * 0.52);
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.height = `${h}px`;

      const proj = geoNaturalEarth1().fitExtent(
        [
          [10, 10],
          [w - 10, h - 10],
        ],
        { type: "Sphere" }
      );
      landPts = getLandPoints(2.3)
        .map(([lon, lat]) => proj([lon, lat]))
        .filter(Boolean);
      cityPts = CITIES.map((c) => ({ ...c, p: proj([c.lon, c.lat]) }));
      arcPts = LINKS.map(([a, b]) => {
        const pa = proj([CITIES[a].lon, CITIES[a].lat]);
        const pb = proj([CITIES[b].lon, CITIES[b].lat]);
        const dist = Math.hypot(pb[0] - pa[0], pb[1] - pa[1]);
        const ctrl = [(pa[0] + pb[0]) / 2, Math.min(pa[1], pb[1]) - dist * 0.22 - 8];
        return { pa, pb, ctrl };
      });
    };

    const draw = (time) => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      // continenti pixelati
      ctx.fillStyle = PINE;
      ctx.globalAlpha = 0.5;
      const s = w < 480 ? 1.4 : 1.7;
      for (const [x, y] of landPts) ctx.fillRect(x, y, s, s);
      ctx.globalAlpha = 1;

      // archi oro tra le piazze
      ctx.strokeStyle = GOLD;
      ctx.lineWidth = 1.1;
      ctx.globalAlpha = 0.65;
      for (const { pa, pb, ctrl } of arcPts) {
        ctx.beginPath();
        ctx.moveTo(pa[0], pa[1]);
        ctx.quadraticCurveTo(ctrl[0], ctrl[1], pb[0], pb[1]);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;

      // città: glow, impulso, etichetta
      cityPts.forEach((c, i) => {
        const [x, y] = c.p;

        const glow = ctx.createRadialGradient(x, y, 0, x, y, 10);
        glow.addColorStop(0, "rgba(201,168,76,0.55)");
        glow.addColorStop(1, "rgba(201,168,76,0)");
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(x, y, 10, 0, Math.PI * 2);
        ctx.fill();

        if (!reduced) {
          const phase = (time / 2200 + i * 0.25) % 1;
          ctx.strokeStyle = GOLD;
          ctx.globalAlpha = (1 - phase) * 0.7;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(x, y, 3 + phase * 13, 0, Math.PI * 2);
          ctx.stroke();
          ctx.globalAlpha = 1;
        }

        ctx.fillStyle = GOLD;
        ctx.beginPath();
        ctx.arc(x, y, 2.6, 0, Math.PI * 2);
        ctx.fill();

        const off = LABEL_OFFSETS[c.name] || { dx: 9, dy: -9 };
        ctx.font = "600 10px sans-serif";
        ctx.textAlign = off.dx >= 0 ? "left" : "right";
        ctx.textBaseline = "middle";
        const label = c.name.toUpperCase();
        ctx.lineWidth = 3;
        ctx.strokeStyle = CREAM;
        ctx.strokeText(label, x + off.dx, y + off.dy);
        ctx.fillStyle = INK;
        ctx.fillText(label, x + off.dx, y + off.dy);
      });
    };

    const loop = (time) => {
      draw(time);
      if (!reduced) raf = requestAnimationFrame(loop);
    };

    rebuild();
    if (reduced) draw(0);
    else raf = requestAnimationFrame(loop);

    const ro = new ResizeObserver(() => {
      rebuild();
      if (reduced) draw(0);
    });
    ro.observe(canvas);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [reduced]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ width: "100%", display: "block" }}
      data-testid="global-globe"
      role="img"
      aria-label="Mappa: Milano, Monaco, Londra, Parigi, Dubai"
    />
  );
}
